import { type WinnerCalcMethod, scaledDelta, scaledRating } from '@topmeeps/shared'
import type { SeasonStatus } from '../api/types'

export const winnerCalcLabel = (m: WinnerCalcMethod): string => {
  switch (m) {
    case 'highest_current_rating':
      return 'Highest current rating'
    case 'most_rating_gained':
      return 'Most rating gained'
    case 'most_wins':
      return 'Most wins'
    case 'highest_win_pct':
      return 'Highest win percentage'
    case 'most_plays':
      return 'Most plays'
  }
}

export const winnerCalcDescription = (m: WinnerCalcMethod): string => {
  switch (m) {
    case 'highest_current_rating':
      return 'Average conservative rating across season-included games where the player has at least one play.'
    case 'most_rating_gained':
      return 'Sum of (end CE − start CE) across season-included games where the player participated.'
    case 'most_wins':
      return 'Count of plays where the player finished in 1st place. Ties at 1st each count as a win.'
    case 'highest_win_pct':
      return 'Wins divided by plays. Players below the minimum-plays threshold are listed but cannot win.'
    case 'most_plays':
      return 'Count of plays the player participated in.'
  }
}

export const seasonStatusFor = (s: { startAt: string; endAt: string }): SeasonStatus => {
  const t = Date.now()
  if (t < new Date(s.startAt).getTime()) return 'scheduled'
  if (t > new Date(s.endAt).getTime()) return 'ended'
  return 'active'
}

export const seasonStatusLabel = (status: SeasonStatus): string =>
  status === 'active' ? 'active' : status === 'scheduled' ? 'scheduled' : 'ended'

export const formatSeasonDateRange = (startAt: string, endAt: string): string => {
  const start = new Date(startAt)
  const end = new Date(endAt)
  const sameYear = start.getFullYear() === end.getFullYear()
  const startStr = start.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: sameYear ? undefined : 'numeric',
  })
  const endStr = end.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
  return `${startStr} → ${endStr}`
}

export const formatStandingMetric = (
  method: WinnerCalcMethod,
  s: { metric: number; qualified: boolean },
): string => {
  switch (method) {
    case 'highest_current_rating':
      // metric is the average raw CE; scale into display SR.
      return `SR ${scaledRating(s.metric)}`
    case 'most_rating_gained': {
      // metric is a raw CE delta; scale.
      const v = scaledDelta(s.metric)
      return `${v > 0 ? '+' : ''}${v} SR`
    }
    case 'most_wins':
      return `${s.metric} win${s.metric === 1 ? '' : 's'}`
    case 'highest_win_pct':
      return `${(s.metric * 100).toFixed(0)}%`
    case 'most_plays':
      return `${s.metric} play${s.metric === 1 ? '' : 's'}`
  }
}
